import { useEffect, useLayoutEffect, useState, useSyncExternalStore } from "react";

/**
 * Eingabesperre nach einem Ansichtswechsel (design/README §6, §10.1, §12): Ein nervöser Doppeltipp darf in der neuen
 * Ansicht nichts auslösen. Die Ansicht ist für die Sperrdauer `inert` (nicht bedienbar, nicht fokussierbar);
 * Werte aus den Tokens `dauer.sperre-ablauf` (300 ms) bzw. `dauer.sperre-kritisch` (500 ms).
 *
 * Gesperrt ist ab dem ersten Bild der neuen Ansicht (bzw. des neuen Schlüssels), nicht erst nach einem Effekt: Ein Tipp
 * kurz nach dem Wechsel trifft nie eine schon bedienbare Ansicht. `aktiv = false` (z. B. geschlossener Dialog) sperrt
 * nichts.
 *
 * Jede laufende Sperre ist zentral vermerkt; `useEingabesperre` zeigt sie an `main` als `aria-busy="true"`
 * (oberflaeche.md §5 „Eingabesperre“, V-16), auch wenn nur ein Teil der Ansicht gesperrt ist (Schritt eines Ablaufs,
 * Dialog).
 */
export function useSperre(art: "ablauf" | "kritisch" = "ablauf", schluessel?: unknown, aktiv = true): boolean {
  const [stand, setStand] = useState(() => ({ schluessel, frei: false }));
  let aktuell = stand;
  if (!Object.is(stand.schluessel, schluessel)) {
    aktuell = { schluessel, frei: false };
    setStand(aktuell);
  }
  const gesperrt = aktiv && !aktuell.frei;

  useEffect(() => {
    if (!aktiv) return;
    const t = window.setTimeout(
      () => setStand((s) => (Object.is(s.schluessel, schluessel) ? { schluessel, frei: true } : s)),
      sperrdauerMs(art),
    );
    return () => window.clearTimeout(t);
  }, [art, schluessel, aktiv]);

  useLayoutEffect(() => {
    if (!gesperrt) return;
    const sperre = {};
    laufend.add(sperre);
    melden();
    return () => {
      laufend.delete(sperre);
      melden();
    };
  }, [gesperrt]);

  return gesperrt;
}

/** `true`, solange die eigene oder irgendeine andere Eingabesperre läuft (für `aria-busy` an `main`). */
export function useEingabesperre(eigene = false): boolean {
  const irgendeine = useSyncExternalStore(abonnieren, () => laufend.size > 0);
  return eigene || irgendeine;
}

function sperrdauerMs(art: "ablauf" | "kritisch"): number {
  const wert = getComputedStyle(document.documentElement).getPropertyValue(`--dj-bewegung-dauer-sperre-${art}`);
  return Number.parseInt(wert, 10) || (art === "kritisch" ? 500 : 300);
}

const laufend = new Set<object>();
const hoerer = new Set<() => void>();

function melden(): void {
  for (const h of hoerer) h();
}

function abonnieren(h: () => void): () => void {
  hoerer.add(h);
  return () => hoerer.delete(h);
}
